import React, { useEffect, useState, useMemo } from 'react';
import { Card, Table, Typography, Spin, Alert } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../../contexts/LanguageContext';
import apiClient from '../../../services/apiClient';
import { getUserInterfacesFromStorage, expandInterfacesWithChildren, hasRole } from '../../../utils/permissionUtils';

const { Title, Text } = Typography;

function formatUtcRange(start, endExclusive) {
  const s = new Date(start);
  const e = new Date(endExclusive);
  return `${s.toLocaleDateString()} – ${e.toLocaleDateString()}`;
}

const UsageBillingSystemReportPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const userInfo = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('userInfo') || '{}');
    } catch {
      return {};
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const interfaces = await getUserInterfacesFromStorage(true);
      const expanded = expandInterfacesWithChildren(interfaces || []);
      const allowed =
        hasRole(userInfo, 'Tenant_Admin') &&
        expanded.some((k) => k === 'reports.billingSystem');
      if (!allowed) {
        navigate('/dashboard', { replace: true });
        return;
      }
      try {
        const res = await apiClient.get('/api/usage-billing/system/monthly');
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.error || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate, userInfo]);

  const companies = data?.companies || [];

  const columns = [
    {
      title: t('companyUserAdmin.companyName') || '公司',
      dataIndex: 'companyName',
      key: 'companyName',
      width: 220,
      fixed: 'left',
    },
    {
      title: t('usageBilling.joinDate') || '計費起點（加入日）',
      key: 'join',
      width: 200,
      render: (_, row) => (
        <Text type="secondary">
          {row.joinDateUtc ? new Date(row.joinDateUtc).toLocaleString() : '—'}
        </Text>
      ),
    },
    {
      title: t('usageBilling.workflowExecution') || '流程執行',
      key: 'exec',
      render: (_, row) => {
        const total = (row.months || []).reduce(
          (sum, m) => sum + (m.counts?.workflowExecution || 0),
          0
        );
        return total;
      },
    },
    {
      title: t('usageBilling.metaOutbound') || 'Meta 對外',
      key: 'meta',
      render: (_, row) => {
        const total = (row.months || []).reduce(
          (sum, m) => sum + (m.counts?.metaOutbound || 0),
          0
        );
        return total;
      },
    },
    {
      title: t('usageBilling.aiCall') || 'AI',
      key: 'ai',
      render: (_, row) => {
        const total = (row.months || []).reduce(
          (sum, m) => sum + (m.counts?.aiCall || 0),
          0
        );
        return total;
      },
    },
    {
      title: t('usageBilling.totalAmount') || '區間應計總額',
      key: 'amt',
      render: (_, row) => {
        const total = (row.months || []).reduce(
          (sum, m) => sum + (m.amounts?.total != null ? Number(m.amounts.total) : 0),
          0
        );
        const cur = row.currency || 'HKD';
        return `${cur} ${total.toFixed(2)}`;
      },
    },
  ];

  const expandedRowRender = (row) => {
    const monthCols = [
      {
        title: t('usageBilling.period') || '計費期間',
        key: 'period',
        render: (_, m) => formatUtcRange(m.periodStartUtc, m.periodEndUtcExclusive),
      },
      {
        title: '#',
        dataIndex: 'monthIndex',
        width: 56,
      },
      {
        title: t('usageBilling.workflowExecution') || '流程執行',
        key: 'c1',
        render: (_, m) => m.counts?.workflowExecution ?? 0,
      },
      {
        title: t('usageBilling.metaOutbound') || 'Meta 對外',
        key: 'c2',
        render: (_, m) => m.counts?.metaOutbound ?? 0,
      },
      {
        title: t('usageBilling.aiCall') || 'AI',
        key: 'c3',
        render: (_, m) => m.counts?.aiCall ?? 0,
      },
      {
        title: t('usageBilling.totalAmount') || '小計',
        key: 'c4',
        render: (_, m) => {
          const cur = row.currency || 'HKD';
          const v = m.amounts?.total != null ? Number(m.amounts.total) : 0;
          return `${cur} ${v.toFixed(2)}`;
        },
      },
    ];
    return (
      <Table
        size="small"
        rowKey={(m) => `${row.companyId}-${m.monthIndex}`}
        columns={monthCols}
        dataSource={row.months || []}
        pagination={false}
      />
    );
  };

  if (loading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 16 }}>
        {t('usageBilling.systemReportTitle') || '全系統用量計費'}
      </Title>
      {error && (
        <Alert type="error" message={error} style={{ marginBottom: 16 }} showIcon />
      )}
      <Card bordered={false}>
        <Table
          rowKey="companyId"
          columns={columns}
          dataSource={companies}
          scroll={{ x: 900 }}
          expandable={{ expandedRowRender }}
          pagination={{ pageSize: 20, showSizeChanger: true }}
        />
      </Card>
    </div>
  );
};

export default UsageBillingSystemReportPage;
